/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/** OCR处理函数：
 * 1. 拍摄/相册上传
 * 2. 删除照片及信息
 *
 * 拍摄/相册上传状态status处理：
 * 1. 成功
 *    - 单张成功
 *    - 相册：both，仅传一张成功；单张，成功；
 *    - 拍摄：单张，成功。
 *    - 两张成功（相册、拍摄）
 *    - 两张**单面成功**（相册，**两个正面或者反面**）
 * 2. 双面超时 - 拍摄超时/相册没传
 * 3. 人像面超时
 *    - 拍摄：仅上传人像
 *    - 相册仅人像，然后没选
 * 4. 国徽面超时
 *    - 拍摄：仅国徽；both，国徽超时
 *    - 相册仅国徽，国徽没选
 * 5. 双面API失败
 *    - 拍摄、相册上传两张，接口异常，前端展示固定页面
 * 6. 人像面API失败
 *    - 注：both，需返回国徽面信息
 * 7. 国徽面API失败
 *    - 注：both，需返回人像面信息
 * 8. 人像与国徽不匹配
 *    - 相册：传国徽，上传了人像
 * 9. 通用失败
 *    - 单面报：人像面识别失败！；双面报：识别失败
 *    - 错误信息展示兜底文案：上传成功后 最高可借额度
 * 10. 单面未知失败
 *    - 相册：both，只传了一张失败
 */
import { asyncCaptureIdentity } from '@/shared/tools/ocr/async';
import { OCRBothInfo } from '@/shared/tools/ocr/types';
import { getMaxQuotaText } from '@/shared/utils/getMaxQuota';
import {
  OCRModeEnum, OcrProcessSourceEnum, UploadStatusEnum, UploadTypeEnum, OcrOperationSourceEnum,
} from '@/shared/tools/ocr/enum';
import { CardUploadStatusEnum } from '../enums';
import { OCRIdentityType, TipMsgType } from '../types';
import { ID_CARD_BACK, ID_CARD_FRONT, TIP_MSG_MAP } from '../constant';

type UploadType = {
  uploadType: UploadTypeEnum,
  frontStatus: CardUploadStatusEnum,
  backStatus: CardUploadStatusEnum,
  isFrontSuccess: boolean,
  isBackSuccess: boolean,
  processSource: OcrProcessSourceEnum,
  operationSource: OcrOperationSourceEnum,
}

type UploadReturnType = {
  frontStatus?: CardUploadStatusEnum,
  backStatus?: CardUploadStatusEnum,
  tipMsg?: TipMsgType, // 注：相册没传：错误信息不变 - 不必回传
  identity?: OCRIdentityType,
  isRemoveMask?: boolean // 是否清除遮罩信息
}
const getOcrMode = (isFrontSuccess: boolean, isBackSuccess: boolean): OCRModeEnum => {
  if (isFrontSuccess) {
    return OCRModeEnum.BACK;
  }
  if (isBackSuccess) {
    return OCRModeEnum.FRONT;
  }
  return OCRModeEnum.BOTH;
};

/** 根据目标字端组装身份信息 */
const getIdentity = (fields: (keyof OCRIdentityType)[] = [], res: OCRBothInfo): OCRIdentityType => {
  const identity: OCRIdentityType = {};
  fields.forEach((field) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    identity[field] = res[field] as any;
  });
  return identity;
};

/** 成功逻辑处理：
   * 1. 单张成功：
   * - 相册：both，仅传一张成功；单张，成功；
   * - 拍摄：单张，成功。
   * 2. 两张成功（相册、拍摄）
   * 3. 两张单面成功（相册：两个正面或者反面)
   * 注：
   * 1. 在相册下，如果要求both，仅一张成功或者两张同面（正面或反面）的成功需特殊处理。
   * 2. 返回信息:仅返回存在信息的那一面数据。
  */
const handleSuccess = (ocrMode: OCRModeEnum, res: OCRBothInfo): UploadReturnType => {
  const { idCardFrontUrl, idCardBackUrl } = res;
  const onlyFront = idCardFrontUrl && !idCardBackUrl;
  const onlyBack = idCardBackUrl && !idCardFrontUrl;
  let tipMsg: TipMsgType;
  if (onlyFront && ocrMode === OCRModeEnum.BOTH) {
    tipMsg = TIP_MSG_MAP[UploadStatusEnum.SUCCESS][OCRModeEnum.FRONT];
  } else if (onlyBack && ocrMode === OCRModeEnum.BOTH) {
    tipMsg = TIP_MSG_MAP[UploadStatusEnum.SUCCESS][OCRModeEnum.BACK];
  } else {
    tipMsg = TIP_MSG_MAP[UploadStatusEnum.SUCCESS][OCRModeEnum.BOTH];
  }

  let fields;
  if (onlyFront) {
    fields = ID_CARD_FRONT;
  } else if (onlyBack) {
    fields = ID_CARD_BACK;
  } else {
    fields = [...ID_CARD_FRONT, ...ID_CARD_BACK];
  }
  return {
    tipMsg,
    // 有人像 或者 要求只要国徽（说明人像是成功的）
    frontStatus: (idCardFrontUrl || ocrMode === OCRModeEnum.BACK)
      ? CardUploadStatusEnum.SUCCESS : CardUploadStatusEnum.PENDING,
    backStatus: (idCardBackUrl || ocrMode === OCRModeEnum.FRONT)
      ? CardUploadStatusEnum.SUCCESS : CardUploadStatusEnum.PENDING,
    identity: getIdentity(fields as (keyof OCRIdentityType)[], res),
    isRemoveMask: !!idCardFrontUrl,
  };
};

/**
 * 相册 上传超时：相册没传（单张或者双张没传）
 */
const handleAlbumTimeout = (
  frontStatus: CardUploadStatusEnum,
  backStatus: CardUploadStatusEnum,
): UploadReturnType => ({
  frontStatus,
  backStatus,
});

/**
 * 拍摄 超时逻辑处理：
 * status2:双面超时：
 * - 拍摄超时
 * status3:人像面超时
 * - 拍摄，仅上传人像
 * status4:国徽面超时
 * - 拍摄仅国徽
 * - both，国徽超时，人像面正常
*/
const handleCameraTimeout = (status: number, res: OCRBothInfo): UploadReturnType => {
  const { idCardFrontUrl } = res;
  switch (status) {
    // 双面超时
    case UploadStatusEnum.TIMEOUT_BOTH:
      return {
        tipMsg: TIP_MSG_MAP[UploadStatusEnum.TIMEOUT_BOTH],
        frontStatus: CardUploadStatusEnum.FAIL,
        backStatus: CardUploadStatusEnum.FAIL,
      };
    // 人像面超时
    case UploadStatusEnum.TIMEOUT_FRONT:
      return {
        tipMsg: TIP_MSG_MAP[UploadStatusEnum.TIMEOUT_FRONT],
        frontStatus: CardUploadStatusEnum.FAIL,
        backStatus: CardUploadStatusEnum.SUCCESS,
      };
    // 国徽面超时
    case UploadStatusEnum.TIMEOUT_BACK:
      return {
        tipMsg: TIP_MSG_MAP[UploadStatusEnum.TIMEOUT_BACK],
        frontStatus: CardUploadStatusEnum.SUCCESS,
        backStatus: CardUploadStatusEnum.FAIL,
        identity: idCardFrontUrl
          ? getIdentity(ID_CARD_FRONT as (keyof OCRIdentityType)[], res)
          : undefined,
        isRemoveMask: !!idCardFrontUrl,
      };
    default:
      return {};
  }
};

/**
 * API错误逻辑处理
 * 1.双面API失败
 * - 拍摄、相册上传两张，接口异常，前端展示固定页面
 * 2.人像面API失败
 * - 拍摄单/双
 * - 相册仅人像
 * - 注：both，需返回国徽面信息
 * 3. 国徽面API失败
 * - 拍摄单/双
 * - 相册单
 * - 注：both，需返回人像面信息
 */
const handleApiError = (
  uploadType: UploadTypeEnum,
  status: number,
  errorMessage: string,
  res: OCRBothInfo,
): UploadReturnType => {
  const { idCardFrontUrl, idCardBackUrl } = res;
  switch (status) {
    // 双面API失败 - 固定页面
    case UploadStatusEnum.API_FAIL_BOTH: {
      const uploadTypeTemp = uploadType === UploadTypeEnum.SDKCamera
        ? UploadTypeEnum.SDKCamera : UploadTypeEnum.Album;
      return {
        tipMsg: TIP_MSG_MAP[UploadStatusEnum.API_FAIL_BOTH][uploadTypeTemp],
        frontStatus: CardUploadStatusEnum.FAIL,
        backStatus: CardUploadStatusEnum.FAIL,
      };
    }
    // 人像面API失败
    case UploadStatusEnum.API_FAIL_FRONT:
      return {
        tipMsg: {
          title: '人像面识别失败！',
          subTitle: errorMessage || `上传成功后 最高可借${getMaxQuotaText()}`,
        },
        frontStatus: CardUploadStatusEnum.FAIL,
        backStatus: CardUploadStatusEnum.SUCCESS,
        identity: idCardBackUrl
          ? getIdentity(ID_CARD_BACK as (keyof OCRIdentityType)[], res)
          : undefined,
      };
    // 国徽面API失败
    case UploadStatusEnum.API_FAIL_BACK:
      return {
        tipMsg: {
          title: '国徽面识别失败！',
          subTitle: errorMessage || `上传成功后 最高可借${getMaxQuotaText()}`,
        },
        frontStatus: CardUploadStatusEnum.SUCCESS,
        backStatus: CardUploadStatusEnum.FAIL,
        identity: idCardFrontUrl
          ? getIdentity(ID_CARD_FRONT as (keyof OCRIdentityType)[], res)
          : undefined,
        isRemoveMask: !!idCardFrontUrl,
      };
    default:
      return {};
  }
};

/**
 * 相册：人像与国徽不匹配
 * 要人像传国徽，并成功了，反之亦然。
*/
const handleNotMatch = (ocrMode: OCRModeEnum, res: OCRBothInfo): UploadReturnType => {
  const { idCardFrontUrl, idCardBackUrl } = res;
  const ocrModeTemp = ocrMode === OCRModeEnum.FRONT ? OCRModeEnum.FRONT : OCRModeEnum.BACK;
  return {
    tipMsg: TIP_MSG_MAP[UploadStatusEnum.NOT_MATCH][ocrModeTemp],
    frontStatus: idCardFrontUrl ? CardUploadStatusEnum.SUCCESS : CardUploadStatusEnum.PENDING,
    backStatus: idCardBackUrl ? CardUploadStatusEnum.SUCCESS : CardUploadStatusEnum.PENDING,
    identity: getIdentity((ocrMode === OCRModeEnum.FRONT
      ? ID_CARD_BACK : ID_CARD_FRONT) as (keyof OCRIdentityType)[], res),
    isRemoveMask: !!idCardFrontUrl,
  };
};

/**
 * 相册：单面未知失败
 * 上传both，但是只传一张报错了。
 */
const singleUnknownError = (): UploadReturnType => ({
  tipMsg: TIP_MSG_MAP[UploadStatusEnum.API_FAIL_UNKNOW],
  frontStatus: CardUploadStatusEnum.PENDING,
  backStatus: CardUploadStatusEnum.PENDING,
});

/** 通用失败处理 */
const handleCommonError = (ocrMode: OCRModeEnum): UploadReturnType => {
  let tipMsg;
  if (ocrMode === OCRModeEnum.FRONT) {
    tipMsg = TIP_MSG_MAP[UploadStatusEnum.FAIL][OCRModeEnum.FRONT];
  } else if (ocrMode === OCRModeEnum.BACK) {
    tipMsg = TIP_MSG_MAP[UploadStatusEnum.FAIL][OCRModeEnum.BACK];
  } else {
    tipMsg = TIP_MSG_MAP[UploadStatusEnum.FAIL][OCRModeEnum.BOTH];
  }
  return {
    tipMsg,
    frontStatus: ocrMode === OCRModeEnum.FRONT ? CardUploadStatusEnum.FAIL : undefined,
    backStatus: ocrMode === OCRModeEnum.BACK ? CardUploadStatusEnum.FAIL : undefined,
  };
};

/** 拍摄、相册上传 */
export const onOCRUpload = async (data: UploadType): Promise<UploadReturnType> => {
  const {
    uploadType,
    frontStatus,
    backStatus,
    isFrontSuccess,
    isBackSuccess,
    processSource,
    operationSource,
  } = data;
  const ocrMode = getOcrMode(isFrontSuccess, isBackSuccess);
  // eslint-disable-next-line no-console
  console.log('onUpload---------', uploadType, ocrMode);
  const res: OCRBothInfo = await asyncCaptureIdentity(
    uploadType,
    ocrMode,
    processSource,
    operationSource,
  );
  // eslint-disable-next-line no-console
  console.log('onUpload---------res', res);
  const { status, errorMessage = '' } = res;
  switch (status) {
    // 成功处理
    case UploadStatusEnum.SUCCESS:
      return handleSuccess(ocrMode, res);
    // 超时处理
    case UploadStatusEnum.TIMEOUT_BOTH:
    case UploadStatusEnum.TIMEOUT_FRONT:
    case UploadStatusEnum.TIMEOUT_BACK:
      // 相册：单张/或者双张照片没传，无需处理，页面展示不变
      if (uploadType === UploadTypeEnum.Album) {
        return handleAlbumTimeout(frontStatus, backStatus);
      }
      // 拍摄
      return handleCameraTimeout(status, res);
    // API失败处理
    case UploadStatusEnum.API_FAIL_BOTH:
    case UploadStatusEnum.API_FAIL_FRONT:
    case UploadStatusEnum.API_FAIL_BACK:
      return handleApiError(uploadType, status, errorMessage, res);
    // 人像与国徽不匹配(相册)
    case UploadStatusEnum.NOT_MATCH:
      return handleNotMatch(ocrMode, res);
    // 单面未知失败(相册)
    case UploadStatusEnum.API_FAIL_UNKNOW:
      return singleUnknownError();
    // 通用失败
    case UploadStatusEnum.FAIL:
    default:
      return handleCommonError(ocrMode);
  }
};

const deleteIdentity = (
  deleteFields: (keyof OCRIdentityType)[] = [],
  identity: OCRIdentityType,
): OCRIdentityType => {
  const identityRes = { ...identity } as OCRIdentityType;
  deleteFields.forEach((field) => {
    if (field in identityRes) {
      delete identityRes[field];
    }
  });
  return identityRes;
};

/** 删除照片及信息 */
export const deleteIdentityCard = (
  mode: OCRModeEnum,
  identity: OCRIdentityType,
): OCRIdentityType => deleteIdentity(
  (mode === OCRModeEnum.FRONT ? ID_CARD_FRONT : ID_CARD_BACK) as (keyof OCRIdentityType)[],
  identity,
);
